import { getEntitiesWithRoutePermission } from './helpers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { EmptyArray } from '@thinkalpha/common/util/empty.js';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import type { InheritedRoutePermissionViewModel, Route } from '@thinkalpha/ui-types/types/admin/lab';
import type { User } from '@thinkalpha/ui-types/types/admin/users';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getSubjectsByIds } from 'src/admin/accounts/access-api';
import { useContainer } from 'src/features/ioc';
import { useUser as useMyUser } from 'src/hooks/state';
import { useSnackbar } from 'src/hooks/useSnackbar';
import { snackbarErrorData } from 'src/lib/util/snackbarErrorData';
// eslint-disable-next-line no-restricted-imports -- TODO: use query hooks instead of direct HTTP imports
import { activateUser, editUser, getUserAccessList } from 'src/queries/http/access-ws/users';
import { getUserQuery, getUserSecurityPackageQuery } from 'src/queries/user';

export function useUser(id: string) {
    return useQuery(getUserQuery(id));
}

export function useToggleActivateUser(user: User) {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const queryClient = useContainer().get('QueryClient');
    const newIsActive = !user?.active;
    return useMutation({
        mutationFn: () => activateUser(user.id, newIsActive),
        onError: (error) => {
            const { options, responseData } = snackbarErrorData(error);
            enqueueSnackbar(
                t('resources::admin::user::activate-user::update-error', `There was an error in updating the user`),
                options,
                responseData,
            );
        },
        onSuccess: () => {
            enqueueSnackbar(t('resources::admin::user::activate-user::update-success', `User updated`), {
                variant: 'success',
                autoHideDuration: 3000,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['usersSearch'] });
            queryClient.invalidateQueries({ queryKey: ['users', user.id] });
        },
    });
}

export function useUpdateUser(userId: string, prevUserValue: User) {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const queryClient = useContainer().get('QueryClient');
    return useMutation({
        mutationFn: (update: User) =>
            editUser(prevUserValue.id, {
                email: update.email,
                firstName: update.firstName,
                lastName: update.lastName,
                roles: update.roles,
            }),
        onError: (error) => {
            const { options, responseData } = snackbarErrorData(error);
            enqueueSnackbar(
                t('admin::user::update-error', `There was an error in updating the user`),
                options,
                responseData,
            );
        },
        onSuccess: () => {
            enqueueSnackbar(t('admin::user::update-success', `User updated`), {
                variant: 'success',
                autoHideDuration: 3000,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['users', userId] });
        },
    });
}

export function useMyAccessList() {
    const myUser = useMyUser();
    const userId = myUser?.id ?? '';

    return useQuery({
        queryKey: ['userAccessList', userId],
        queryFn: () => getUserAccessList(userId),
        enabled: !!userId,
    });
}

export function useIsUserAdmin(user: UserDetails | undefined): [isUserAdmin: boolean, isUserAccessLoading: boolean] {
    const { data: securityPackage, isLoading: isSecurityPackageLoading } = useQuery(
        getUserSecurityPackageQuery(user?.id),
    );
    const isUserAnAdmin = useMemo(() => {
        if (!securityPackage?.accessList) return false;
        return securityPackage.accessList.some((a) => a.type === 'firm' && a.permission === 'admin');
    }, [securityPackage]);
    if (user?.isSuperuser) return [true, false];
    return [isUserAnAdmin, isSecurityPackageLoading];
}
export const useUserInheritedRouteAccess = (
    userId: string,
    routes: Route[] | undefined,
    firms: FirmWithParentsAndChildren[] | undefined,
) => {
    // get users access list - this includes groups / firms, etc.
    const { data: userAccessList } = useQuery({
        queryKey: ['user-access-List', userId],
        queryFn: () => getUserAccessList(userId),
        enabled: !!userId,
    });

    // convert all access entries to ids and filter out user and special
    const userAccessListIds = useMemo(() => {
        if (!routes || !userAccessList) return EmptyArray;
        return userAccessList
            .filter((access) => access.type !== 'user' && access.type !== 'special')
            .map(({ id }) => id);
    }, [routes, userAccessList]);

    //pass userAccessListIds to get all entities (excluding users and special) that the user has access to
    const { data: userAccessEntities } = useQuery({
        queryKey: ['entities', userId],
        queryFn: () => getSubjectsByIds(userAccessListIds),
        enabled: Boolean(userAccessListIds && userAccessListIds.length),
    });

    // map over routes to get all user-accessible entities that have permissions to routes, and then map to InheritedRoutePermissionViewModel
    const userInheritedRouteAccess: InheritedRoutePermissionViewModel[] = useMemo(() => {
        if (!userAccessEntities || !routes || !firms) return EmptyArray;
        return routes
            .map((route) => {
                return {
                    ...route,
                    // This is the firm that the route-firm configuration belongs to
                    firm: firms.find((firm) => firm.id === route.firmId)?.name ?? '',
                    // array of all entities that have any permission to the route
                    entityAccess: getEntitiesWithRoutePermission(route)
                        .map((e) => {
                            const matchedEntity = userAccessEntities.find((entity) => entity.id === e.id);
                            if (!matchedEntity) return;
                            return {
                                id: matchedEntity.id,
                                entityName:
                                    'name' in matchedEntity
                                        ? matchedEntity.name
                                        : 'username' in matchedEntity && matchedEntity.username
                                          ? matchedEntity.username
                                          : '',
                                type: matchedEntity.type,
                                permission: e.access,
                            };
                        })
                        .filter((x) => !!x),
                };
            })
            .filter((x) => x.entityAccess.length > 0);
    }, [userAccessEntities, routes, firms]);

    return userInheritedRouteAccess;
};
